import { resolveOrganisationDisplayName } from "../../utils/string";
import { decodeContractorPackageLocalContentTarget, encodeContractorPackageLocalContentTarget } from "../contractorPackageLocalContentTarget";
import { decodeFinancial, encodeFinancial } from "../financial";
import { decodeOrganisation, encodeOrganisation } from "../organisation";
import { decodePackageLabourHourCommitment, encodePackageLabourHourCommitment } from "../packageLabourHourCommitment";
import { decodePackageLocalContent, encodePackageLocalContent } from "../packageLocalContent";
import { decodePackagePolicy, encodePackagePolicy } from "../packagePolicy";
import { decodePackageSocialSpendCommitment, encodePackageSocialSpendCommitment } from "../packageSocialSpendCommitment";
import { decodeProfile, encodeProfile } from "../profile";
export const decodeContractorPackageSetupLineItem = async (contractorPackage, getImageUrl) => {
    const org = contractorPackage.organisation
        ? await decodeOrganisation(contractorPackage.organisation, getImageUrl)
        : undefined;
    const cp = await decodeContractorPackage(contractorPackage.contractor_package, getImageUrl);
    const prof = contractorPackage.profile
        ? await decodeProfile(contractorPackage.profile)
        : undefined;
    return {
        contractorPackage: cp,
        organisation: org,
        profile: prof
    };
};
export const encodeOneTimeContractorPackage = (contractorPackage, organisation, profile) => {
    // contractor_id must match organisation_id when both are sent (e.g. create/update with invite).
    // Stale contractorId can remain on the package if the user re-ran ABN lookup: each AuGov response
    // generates a new transient organisation_id while the package still held the previous id.
    const packageForEncode = organisation?.id && organisation.id.length > 0
        ? { ...contractorPackage, contractorId: organisation.id }
        : contractorPackage;
    const cp = encodeContractorPackage(packageForEncode);
    const org = organisation ? encodeOrganisation(organisation) : undefined;
    const prof = profile ? encodeProfile(profile) : undefined;
    return {
        contractor_package: cp,
        organisation: org,
        profile: prof
    };
};
export const decodeContractorPackage = async (contractorPackage, getImageUrl) => {
    const presignedUrl = contractorPackage.organisation_logo &&
        contractorPackage.organisation_logo.length > 0
        ? await getImageUrl(contractorPackage.organisation_logo)
        : "";
    const policySubmissions = contractorPackage.policies?.map((p) => decodePackagePolicy(p)) || [];
    return {
        closedDate: contractorPackage.closed_date,
        commitmentsEnvironmental: (contractorPackage.commitments_environmental || []).map((e) => ({
            contractorPackageId: e.contractor_package_id,
            created: e.created,
            id: e.package_environmental_commitment_id,
            projectEnvironmentalCommitmentId: e.project_environmental_commitment_id,
            projectId: e.project_id
        })),
        commitmentsHours: contractorPackage.commitments_hours.map((s) => decodePackageLabourHourCommitment(s)),
        commitmentsSpend: contractorPackage.commitments_spend.map((s) => decodePackageSocialSpendCommitment(s)),
        contractorAbn: contractorPackage.abn,
        contractorDisplayName: resolveOrganisationDisplayName(contractorPackage.organisation_name, contractorPackage.canonical_name),
        contractorId: contractorPackage.contractor_id,
        contractorLogo: presignedUrl,
        contractorName: contractorPackage.organisation_name,
        created: new Date(contractorPackage.created),
        depth: contractorPackage.depth,
        enabledLocalContent: contractorPackage.enabled_local_content,
        estimatedProgress: contractorPackage.estimated_progress,
        financial: decodeFinancial(contractorPackage.financial),
        firstReportMonth: contractorPackage.first_report_month,
        id: contractorPackage.contractor_package_id,
        isAccepted: contractorPackage.is_accepted,
        isClosed: contractorPackage.is_closed ?? false,
        isDraft: contractorPackage.is_draft,
        localContent: contractorPackage?.local_content?.map((lc) => decodePackageLocalContent(lc)) || [],
        localContentTargets: contractorPackage.local_content_targets.map((t) => decodeContractorPackageLocalContentTarget(t)),
        packageContact: contractorPackage.package_contact,
        packageDescription: contractorPackage.package_description,
        packageEndDate: contractorPackage.package_end_date,
        packageNumber: contractorPackage.package_number,
        packageStartDate: contractorPackage.package_start_date,
        packageType: contractorPackage.package_type,
        parentPackageId: contractorPackage.parent_package_id,
        policies: policySubmissions,
        projectId: contractorPackage.project_id,
        reportExists: contractorPackage.report_exists,
        representativeEmail: contractorPackage.representative_email,
        sendInvite: contractorPackage.send_invite,
        treePath: contractorPackage.tree_path,
        useLabourHour: contractorPackage.use_labour_hour,
        useLocalContent: contractorPackage.use_local_content,
        usePolicies: contractorPackage.use_policy,
        useSocialSpend: contractorPackage.use_social_spend,
        useSustainability: contractorPackage.use_sustainability
    };
};
export const encodeContractorPackage = (contractorPackage) => {
    return {
        abn: contractorPackage.contractorAbn,
        closed_date: contractorPackage.closedDate,
        commitments_environmental: (contractorPackage.commitmentsEnvironmental || []).map((e) => ({
            contractor_package_id: e.contractorPackageId,
            created: e.created,
            package_environmental_commitment_id: e.id,
            project_environmental_commitment_id: e.projectEnvironmentalCommitmentId,
            project_id: e.projectId
        })),
        commitments_hours: contractorPackage.commitmentsHours.map((s) => encodePackageLabourHourCommitment(s)),
        commitments_spend: contractorPackage.commitmentsSpend.map((s) => encodePackageSocialSpendCommitment(s)),
        contractor_id: contractorPackage.contractorId,
        contractor_package_id: contractorPackage.id,
        created: contractorPackage.created,
        depth: contractorPackage.depth,
        enabled_local_content: contractorPackage.enabledLocalContent,
        estimated_progress: contractorPackage.estimatedProgress,
        financial: encodeFinancial(contractorPackage.financial),
        financial_id: contractorPackage.financial.id,
        first_report_month: contractorPackage.firstReportMonth,
        is_accepted: contractorPackage.isAccepted,
        is_closed: contractorPackage.isClosed,
        is_draft: contractorPackage.isDraft,
        local_content: contractorPackage.localContent?.map((lc) => encodePackageLocalContent(lc)) || [],
        local_content_targets: contractorPackage.localContentTargets.map((t) => encodeContractorPackageLocalContentTarget(t)),
        organisation_logo: contractorPackage.contractorLogo,
        organisation_name: contractorPackage.contractorName,
        package_contact: contractorPackage.packageContact,
        package_description: contractorPackage.packageDescription,
        package_end_date: contractorPackage.packageEndDate,
        package_number: contractorPackage.packageNumber,
        package_start_date: contractorPackage.packageStartDate,
        package_type: contractorPackage.packageType,
        parent_package_id: contractorPackage.parentPackageId,
        policies: contractorPackage.policies?.map((p) => encodePackagePolicy(p)) || [],
        project_id: contractorPackage.projectId,
        report_exists: contractorPackage.reportExists,
        representative_email: contractorPackage.representativeEmail,
        send_invite: contractorPackage.sendInvite,
        tree_path: contractorPackage.treePath,
        use_labour_hour: contractorPackage.useLabourHour,
        use_local_content: contractorPackage.useLocalContent,
        use_policy: contractorPackage.usePolicies,
        use_social_spend: contractorPackage.useSocialSpend,
        use_sustainability: contractorPackage.useSustainability
    };
};
